import React, { useState, useEffect } from "react";
import { Alert, Box, TextField, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import ErrorMsg from "../../components/Commons/ErrorMsg";
import { useGlobalState } from "../../contexts/GlobalContext";
import { updateUserPassword, fetchResetPassword } from "../../utils/helpers/api";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Routes } from "../../utils/constants";
import { AxiosResponse } from "axios";

// Define the type for the error state
interface IErrorState {
  new_password: string;
  password_confirm: string | JSX.Element;
}

// Define the type for the error state
interface IErrorState {
  new_password: string;
  password_confirm: string | JSX.Element;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get("ticket"); 
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery("(max-width:756px)");
  const [state, setState] = useState<IResetPassPageState>({
    new_password: "",
    password_confirm: "",
    isSucess: false,
  });
  const [error, setError] = useState<IErrorState>({
    new_password: "",
    password_confirm: "",
  });
  const { setStateValue, globalState } = useGlobalState();
  const { user } = globalState;
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get("email") ?? "";
  const handleChangeValue = (field: string, value?: string) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  useEffect(() => {
    document.title = "宣伝会議 : アカウント管理 | パスワード設定  ";
  }, []);

  const hanldeChangePassword = async () => {
    setStateValue("loader", true);
    try {
      /*
      const result = await fetchChangePasswordV2(
        user?.user_id ?? "",
        state.new_password
      );
      */
      hanldeUpdateSsoUserPassword();
      setState((prevState) => ({
        ...prevState,
        isSucess: true,
        password: "",
        new_password: "",
        password_confirm: "",
      }));
    } catch (error) {
      return error;
    } finally {
      setStateValue("loader", false);
    }
  };

  const hanldeUpdateSsoUserPassword = async () => {
    try {
      await updateUserPassword(
        user.email,
        user?.user_id ?? "",
        state.new_password
      );
    } catch (error) {
      return error;
    }
  };

  const handleResetPassword = async () => {
    setStateValue("loader", true);
    const encodedEmail = decodeURIComponent(email);
    
    try {
      const response = await fetchResetPassword(encodedEmail, state.new_password, ticketId ?? "") as any;      
      if (response.status === 200) {
        // localStorage.removeItem("resetPasswordEmail");
        setState((prevState) => ({
          ...prevState,
          isSucess: true,
          new_password: "",
          password_confirm: "",
        }));
        navigate(Routes.RESET_PASS_COMPLETED);
      }
    } catch (error) {
      setError(prev => ({
        ...prev,
        password_confirm: (
          <span>
            「すでに更新済みか、有効期限が切れている可能性があります。パスワードの再設定を行う場合は、
            <a href="https://i.sendenkaigi.com/user/guest/password_remind" target="_blank" rel="noopener noreferrer">
              パスワード再設定
            </a>{" "}
            から再実施をお願いします。」
          </span>
        ),
      }));
      return error;
    } finally {
      setStateValue("loader", false);
    }
  };

  const handleSubmit = () => {
    let isValidate = true;
    let errorPassMess = "";
    let errorPassConfirmMess = "";
    let errorNewPassMess = "";

    const checkRulePwd = (pwd: string) => {
      const upperCase = /[A-Z]/.test(pwd);
      const lowerCase = /[a-z]/.test(pwd);
      const number = /[0-9]/.test(pwd);
      const specialChar = /[@$!%*?&]/.test(pwd);
      const categories = [upperCase, lowerCase, number, specialChar].filter(
        Boolean
      ).length;

      return categories < 3 ? false : true;
    };

    if (!state.new_password) {
      isValidate = false;
      errorNewPassMess = "新しいパスワード欄は必須です。";
    } else if (
      state.new_password.length < 8 ||
      !checkRulePwd(state.new_password)
    ) {
      isValidate = false;
      errorNewPassMess =
        "大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。";
    }
    // else if (!Regex.RULE.PASSWORD.test(state.new_password)) {
    //   isValidate = false;
    //   errorNewPassMess =
    //     "パスワードには英数字と特殊文字 (@,$,!,%,*,?,&) を含めてください。";
    // }
    else if (state.new_password.length > 50) {
      isValidate = false;
      errorNewPassMess = "新しいパスワード欄は50文字を超えてはいけません。";
    } else {
      errorNewPassMess = "";
    }

    if (!state.password_confirm) {
      isValidate = false;
      errorPassConfirmMess = "パスワード:確認欄は必須です。";
    } else if (
      state.password_confirm.length < 8 ||
      !checkRulePwd(state.password_confirm)
    ) {
      isValidate = false;
      errorPassConfirmMess =
        "大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。";
    }
    // else if (!Regex.RULE.PASSWORD.test(state.password_confirm)) {
    //   isValidate = false;
    //   errorPassConfirmMess =
    //     "パスワードには英数字と特殊文字 (@,$,!,%,*,?,&) を含めてください。";
    // }
    else if (state.password_confirm.length > 50) {
      isValidate = false;
      errorPassConfirmMess = "パスワード:確認欄は50文字を超えてはいけません。";
    } else if (state.new_password !== state.password_confirm) {
      errorPassConfirmMess =
        "パスワード:確認欄が新しいパスワードと一致しません。";
      isValidate = false;
    } else {
      errorPassConfirmMess = "";
    }

    setError({
      password_confirm: errorPassConfirmMess,
      new_password: errorNewPassMess,
    });

    if (isValidate) {
      handleResetPassword()
    } else {
      setState((prevState) => ({
        ...prevState,
        password: "",
        new_password: "",
        password_confirm: "",
        isSucess: false,
      }));
    }
  };

  return (
    <Box className="page__setting-password">
      <div>
        <div className="header-tit">
          {t("features.passwordSetting.headTit")}
        </div>
        {state.isSucess && (
          <div className="p-2">
            <Alert
              sx={{
                bgcolor: "#dff0d8",
                color: "#3c763d",
              }}
              icon={false}
              severity="success"
              onClose={() => {
                setState((prevState) => ({
                  ...prevState,
                  isSucess: false,
                }));
              }}
            >
              {t("features.passwordSetting.successText")}
            </Alert>
          </div>
        )}
        <div className="form-horizontal mt-3">

          <div className="form-group row mt-2">
            <div className="col-sm-3 form-label">
              <label>{t("features.passwordSetting.newPass")}</label>
            </div>

            <div className="col-sm-9 ">
              <TextField
                type="password"
                id="new_password"
                name="new_password"
                autoComplete="email"
                fullWidth={isSmallScreen}
                className="w-100"
                value={state.new_password}
                onChange={(e) =>
                  handleChangeValue("new_password", e.target.value)
                }
                size="small"
                inputProps={{
                  className: "form-control mt4",
                  maxLength: 100,
                  style: { height: 15 },
                  "aria-hidden": false,
                }}
              />
              <ErrorMsg error={error.new_password} />
              {!error.new_password && (
                <span className="txt-12">
                  大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。
                </span>
              )}
              <p className="txt-sm txt-gray mt16">
                {t("features.passwordSetting.passwordHint")}
              </p>

              <TextField
                type="password"
                id="password_confirm"
                name="password_confirm"
                fullWidth={isSmallScreen}
                className="w-100"
                value={state.password_confirm}
                onChange={(e) =>
                  handleChangeValue("password_confirm", e.target.value)
                }
                size="small"
                inputProps={{
                  className: "form-control mt4",
                  maxLength: 100,
                  style: { height: 15 },
                  "aria-hidden": false,
                }}
              />
              <ErrorMsg error={error.password_confirm} />
            </div>
          </div>
        </div>
        <hr />

        <Box className="center mt-4">
          <Button
            onClick={handleSubmit}
            className={`btn btn-primary form-control btn-md btn-submit`}
          >
            {t("common.buttonTextSend")}
          </Button>
        </Box>
      </div>
    </Box>
  );
};

export default ResetPassword;
